import { UserPlus, UserMinus, Info, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/app/_module/components/ui/tooltip";
import { AgentAssignmentDropdown } from "./AgentAssignmentDropdown";

interface AssignmentActionsProps {
  onAssign: (agentId: string, cleanSlate?: boolean) => void;
  onUnassign?: (cleanSlate?: boolean, includeClosed?: boolean) => void;
  /** Counts sold/closed leads in the selection so the dialog can say what will be kept. */
  onPreviewUnassign?: () => Promise<{ total: number; closedCount: number }>;
  selectedLeadsCount: number;
  selectedAssignedCount?: number;
  selectedUnassignedCount?: number;
  isAssigning?: boolean;
  isUnassigning?: boolean;
  hasAssignedLeads?: boolean;
  hasUnassignedLeads?: boolean;
  /** Optional controlled agent selection so InsuraBot can pre-fill the suggested agent. */
  agentId?: string;
  onAgentIdChange?: (id: string) => void;
}

/**
 * Sold/closed leads stay with their agent unless this is ticked. It used to be half a
 * sentence plus a bare checkbox; Brandon confirmed past it and read the 8 he couldn't
 * unassign from Vincenzo as "it won't let me" (2026-10-23).
 */
export function ClosedLeadsOption({ count, all, checked, onChange }: { count: number; all: boolean; checked: boolean; onChange: (v: boolean) => void }) {
  const who = all ? (count === 1 ? "This lead is" : `All ${count} are`) : count === 1 ? "1 of these is" : `${count} of these are`;
  return (
    <label className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        <span className="font-medium">Include sold/closed leads</span>
        <span className="block text-amber-800">
          {who} sold or closed (a policy, underwriting or awaiting payment).{" "}
          {checked ? "They will be unassigned too." : "They stay with their agent unless you tick this."}
        </span>
      </span>
    </label>
  );
}

export const AssignmentActions = function ({
  onAssign,
  onUnassign,
  onPreviewUnassign,
  selectedLeadsCount,
  selectedAssignedCount = 0,
  selectedUnassignedCount = 0,
  isAssigning = false,
  isUnassigning = false,
  hasAssignedLeads = false,
  hasUnassignedLeads = true,
  agentId,
  onAgentIdChange,
}: AssignmentActionsProps) {
  const [internalAgentId, setInternalAgentId] = useState<string>("");
  // Controlled when the parent passes agentId/onAgentIdChange (InsuraBot pre-fill),
  // otherwise falls back to local state.
  const selectedAgentId = agentId !== undefined ? agentId : internalAgentId;
  const setSelectedAgentId = onAgentIdChange ?? setInternalAgentId;
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [cleanSlate, setCleanSlate] = useState(false);
  // Sold/closed leads in the selection: kept with their agent unless the owner ticks the box.
  const [closedCount, setClosedCount] = useState<number | null>(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  // Latest preview fn in a ref: the parent passes a fresh function each render, and the
  // preview should run once per dialog open, not on every parent re-render.
  const previewRef = useRef(onPreviewUnassign);
  previewRef.current = onPreviewUnassign;
  useEffect(() => {
    if (!showUnassignConfirm) return;
    setIncludeClosed(false);
    setClosedCount(null);
    const preview = previewRef.current;
    if (!preview) return;
    let cancelled = false;
    preview().then((r) => { if (!cancelled) setClosedCount(r?.closedCount ?? 0); }).catch(() => { if (!cancelled) setClosedCount(0); });
    return () => { cancelled = true; };
  }, [showUnassignConfirm]);

  // Either action in flight locks BOTH buttons — prevents rapid clicking from
  // firing overlapping assign/unassign requests against the same selection.
  const busy = isAssigning || isUnassigning;

  const handleAssign = () => {
    if (selectedAgentId && !busy) {
      onAssign(selectedAgentId, cleanSlate);
      setSelectedAgentId("");
    }
  };

  const handleUnassign = () => {
    if (onUnassign && !busy) {
      onUnassign(cleanSlate, includeClosed);
      setShowUnassignConfirm(false);
    }
  };

  const showUnassign = hasAssignedLeads && onUnassign;
  const unassignCount = selectedAssignedCount > 0 ? selectedAssignedCount : selectedLeadsCount;
  // Every selected lead is sold/closed: Unassign would do nothing until the box is ticked.
  const allClosed = !!closedCount && closedCount >= unassignCount;

  return (
    <Card>
      <CardContent className="px-3 py-2.5">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">
              {selectedLeadsCount} selected
            </span>
            {hasAssignedLeads && hasUnassignedLeads && (
              <span className="text-[11px] text-gray-400 whitespace-nowrap truncate">
                {selectedUnassignedCount} new · {selectedAssignedCount} assigned
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 min-w-0 justify-end">
            {showUnassign && (
              <>
                <Button
                  onClick={() => setShowUnassignConfirm(true)}
                  disabled={busy}
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-2"
                >
                  <UserMinus className="h-3.5 w-3.5 mr-1" />
                  {isUnassigning ? "..." : "Unassign"}
                </Button>
                <AlertDialog open={showUnassignConfirm} onOpenChange={setShowUnassignConfirm}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Unassign {unassignCount} lead{unassignCount !== 1 ? "s" : ""}?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This will remove the leads from their assigned agents and return them to the unassigned pool.
                        {cleanSlate && " Fresh Lead is ON — the agent will see a clean lead with no previous activity."}
                        {closedCount === null && onPreviewUnassign && " Checking for sold leads…"}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    {!!closedCount && (
                      <ClosedLeadsOption count={closedCount} all={allClosed} checked={includeClosed} onChange={setIncludeClosed} />
                    )}
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleUnassign}
                        disabled={busy || (allClosed && !includeClosed)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {isUnassigning ? "Unassigning..." : "Unassign"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            <div className="flex-1 basis-[140px] min-w-0 max-w-[220px]">
              <AgentAssignmentDropdown
                value={selectedAgentId}
                onValueChange={setSelectedAgentId}
                placeholder="Select agent"
                className="w-full h-8 text-xs [&_svg]:h-3.5 [&_svg]:w-3.5"
                disabled={busy}
              />
            </div>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setCleanSlate(!cleanSlate)}
                    className={`h-8 flex items-center gap-1 px-2 rounded-md border text-xs font-medium transition-all ${
                      cleanSlate
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-gray-200 text-gray-400 hover:border-gray-300"
                    }`}
                  >
                    <Sparkles className="w-3 h-3" />
                    Fresh
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-sm text-xs leading-relaxed">
                  <p className="font-medium mb-1">Fresh Lead Mode</p>
                  <p>When enabled, the assigned agent will see a completely clean lead — as if no one has ever worked it before.</p>
                  <ul className="mt-1.5 space-y-0.5 text-gray-400">
                    <li>- Previous notes will be hidden</li>
                    <li>- Call history will be hidden</li>
                    <li>- SMS and email history will be hidden</li>
                    <li>- Activity timeline will start fresh</li>
                  </ul>
                  <p className="mt-1.5 text-gray-400">Nothing is permanently deleted. An owner can restore all hidden activity at any time.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              onClick={handleAssign}
              disabled={!selectedAgentId || busy}
              size="sm"
              className="h-8 px-3"
            >
              <UserPlus className="h-3.5 w-3.5 mr-1" />
              {isAssigning ? "Assigning..." : hasAssignedLeads && !hasUnassignedLeads ? "Reassign" : "Assign"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
